import strings from '../../strings/strings.json';
import * as React from 'react';
import { AuthContext } from '../../contextProviders/AuthContext';
import styled from 'styled-components';
import { breakpoints, sizes, theme } from '../../theme';
import { BodyLarge, FlexColLg, FlexMd, Headline2 } from '../Elements';
import { getFriendlyRoleName, ROLE_DETAILS, RoleType } from '../../models/User';
import RadioFields from '../forms/RadioFields';
import useNeveForm from '../forms/NeveForm';
import { ErrorMessage } from '../forms/FormComponents';
import { useNavigate } from 'react-router-dom';
import Button from './Button';

type RoleInput = {
    role: RoleType | null;
};

export default function ChangeRoleButton(): JSX.Element {
    const { userData, setActiveRole } = React.useContext(AuthContext);
    const navigate = useNavigate();

    const [showRoleSelectOverlay, setShowRoleSelectOverlay] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);

    const roles: RoleType[] = [...userData.roles];

    const { register, handleSubmit, reset } = useNeveForm<RoleInput>({
        role: null,
    });

    function openModal() {
        setShowRoleSelectOverlay(true);
        document.body.style.overflow = 'hidden';
    }

    function closeModal() {
        reset();
        setError(null);
        setShowRoleSelectOverlay(false);
        document.body.style.overflow = 'visible';
    }

    const submitRoleChange = (formData: RoleInput) => {
        if (!formData.role) {
            setError(strings.changeRoleOverlay.noRoleSelectedError);
            document.body.style.overflow = 'visible';
            return;
        }

        const newRole = formData.role;
        if (!roles.includes(newRole)) {
            setError(strings.changeRoleOverlay.invalidRoleError);
            return;
        }

        setActiveRole(newRole);
        setError(null);
        reset();
        setShowRoleSelectOverlay(false);
        navigate('/');
        document.body.style.overflow = 'visible';
    };

    function toggleTwoRoles(otherRole: RoleType) {
        // If currently LEARNER, switch to the other role
        const roleToActivate = userData.activeRole === RoleType.LEARNER ? otherRole : RoleType.LEARNER;
        setActiveRole(roleToActivate);
        navigate('/');
    }

    const renderSwitchRoleButton = (): JSX.Element | null => {
        switch (roles.length) {
            case 1:
                // Only LEARNER role, don't show button
                return null;
            case 2: {
                // Find the other role (user always has LEARNER)
                const otherRole = roles.find((r) => r !== RoleType.LEARNER) || RoleType.LEARNER;

                const buttonLabel =
                    userData.activeRole === RoleType.LEARNER
                        ? `${strings.switchRoleButton.switchTo} ${otherRole === RoleType.ORGANISATION_ADMIN ? `${strings.switchRoleButton.orgAdmin}` : `${strings.switchRoleButton.trainer}`}`
                        : `${strings.switchRoleButton.switchTo} ${strings.switchRoleButton.learner}`;
                return (
                    <Button
                        title={buttonLabel}
                        aria={`${strings.switchRoleButton.switchButtonAria} to ${buttonLabel}`}
                        onClick={() => toggleTwoRoles(otherRole)}
                    />
                );
            }
            default:
                // More than 2 roles, open the modal
                return (
                    <Button

                        title={strings.switchRoleButton.switchButtonTitle}
                        aria={strings.switchRoleButton.switchButtonAria}
                        onClick={openModal}
                    />
                );
        }
    };

    const switchRoleButton = renderSwitchRoleButton();

    return (
        <>
            {switchRoleButton}
            {showRoleSelectOverlay && (
                <Modal onSubmit={handleSubmit(submitRoleChange)}>
                    <ModalBody>
                        <ModalHeader>{strings.changeRoleOverlay.headerText}</ModalHeader>
                        <BodyLarge>
                            {strings.changeRoleOverlay.subtitleText}
                            {userData.activeRole ? getFriendlyRoleName(userData.activeRole, true) : null}
                        </BodyLarge>
                        <RadioFields
                            options={ROLE_DETAILS.filter((roleDetail) =>
                                roles.includes(roleDetail.name as RoleType),
                            ).map((roleDetail) => ({
                                value: roleDetail.name,
                                title: roleDetail.friendlyName,
                                description: roleDetail.description,
                                aria: roleDetail.name,
                                inputProps: register('role'),
                                name: 'role',
                            }))}
                        />
                        {error && <ErrorMessage>{error}</ErrorMessage>}
                        <ButtonContainer>
                            <Button
                                title={strings.changeRoleOverlay.backButtonTitle}
                                aria={strings.changeRoleOverlay.backButtonAria}
                                onClick={closeModal}
                            />
                            <Button
                                colour='PRIMARY'
                                title={strings.changeRoleOverlay.submitButtonTitle}
                                aria={strings.changeRoleOverlay.submitButtonAria}
                                type="submit"
                            />
                        </ButtonContainer>
                    </ModalBody>
                </Modal>
            )}
        </>
    );
}

const ModalBody = styled(FlexColLg)`
    max-width: 600px;
`;

const Modal = styled.form`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${sizes.spacingLg};
    position: fixed;
    top: 80px;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${theme.cardBackground};
    z-index: 10;
    padding: ${sizes.spacingRg};
    overflow-y: auto;
    @media (max-width: ${breakpoints.sm}) {
        top: 100px;
    }
`;

const ModalHeader = styled(Headline2)`
    padding-top: ${sizes.spacingLg};
    text-align: center;
    @media (max-width: ${breakpoints.sm}) {
        padding-top: ${sizes.spacingSm};
    }
`;

const ButtonContainer = styled(FlexMd)`
    margin-bottom: 100px;
    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
        width: 100%;
        margin-bottom: 150px;
    }
`;
